/**
 * Camera Nodes - Render a scene to video output
 *
 * Config-driven camera nodes for different camera types.
 * Takes scene input, has camera controls, outputs video.
 * Registers ThreeRenderer with video registry for visualization.
 * Flow: Scene -> Camera (videoOut with visualization)
 */

import { BaseNode } from '@nodes/BaseNode';
import { internals, params } from '@/nodes/TypedEndpoints';
import type { NodeDef } from '@runtime/IRuntime';
import type { ThreeDependencies } from '../threejsPlugin';
import type { Three, PerspectiveCamera, OrthographicCamera } from '../types';
import { ThreeRenderer } from '../ThreeRenderer';
import { Payload } from '@/graph/Payload';
import type { INode } from '@/nodes/INode';
import type { IEndpoint } from '@/runtime/IEndpoint';

// Base endpoints shared by all camera types
const baseInternals = internals({
  sceneIn: { dataType: 'three:scene', isInput: true, label: 'Scene' },
  videoOut: { dataType: 'video', isOutput: true }
});

// Default camera angle: nice 3/4 view from above
const DEFAULT_THETA = 0.8;   // ~45° around
const DEFAULT_PHI = 1.2;     // Looking down at ~20° from horizontal
const DEFAULT_RADIUS = 12;   // Good viewing distance

// Orbit control params shared by all cameras
const orbitParams = params({
  theta: { value: DEFAULT_THETA, min: -Math.PI, max: Math.PI, step: 0.01, label: 'θ Horizontal', group: 'Orbit' },
  phi: { value: DEFAULT_PHI, min: 0.1, max: Math.PI - 0.1, step: 0.01, label: 'φ Vertical', group: 'Orbit' },
  roll: { value: 0, min: -Math.PI, max: Math.PI, step: 0.01, label: 'Roll', group: 'Orbit' },
  radius: { value: DEFAULT_RADIUS, min: 0.5, max: 50, step: 0.1, label: 'Distance', group: 'Orbit' },
  panX: { value: 0, min: -50, max: 50, step: 0.1, label: 'Pan X', group: 'Pan' },
  panY: { value: 0, min: -50, max: 50, step: 0.1, label: 'Pan Y', group: 'Pan' },
  panZ: { value: 0, min: -50, max: 50, step: 0.1, label: 'Pan Z', group: 'Pan' },
  resetCamera: { dataType: 'boolean', value: false, label: 'Reset', group: 'Orbit' },
  showAxes: { dataType: 'boolean', value: false, label: 'Axes', group: 'Display' },
  showGrid: { dataType: 'boolean', value: false, label: 'Grid', group: 'Display' }
});

// Clipping plane params shared by all cameras
const clipParams = params({
  near: { value: 0.1, min: 0.01, max: 10, step: 0.01, group: 'Camera' },
  far: { value: 1000, min: 10, max: 10000, step: 10, group: 'Camera' }
});

// ─────────────────────────────────────────────────────────────
// Camera Configs
// ─────────────────────────────────────────────────────────────

type CameraType = PerspectiveCamera | OrthographicCamera;

interface CameraConfig {
  type: string;
  shortName: string;
  /** Three.js camera class name */
  cameraClass: 'PerspectiveCamera' | 'OrthographicCamera';
  /** Camera-specific params */
  params: Record<string, any>;
  /** Build camera from three and params */
  buildCamera: (three: Three, params: Record<string, any>) => CameraType;
  /** Update camera when params change */
  updateCamera: (camera: CameraType, params: Record<string, any>, changedParam: string) => void;
}

const perspectiveParams = params({
  fov: { value: 50, min: 10, max: 120, step: 1, group: 'Camera' }
});

const orthographicParams = params({
  orthoSize: { value: 5, min: 0.5, max: 50, step: 0.1, label: 'Size', group: 'Camera' }
});

const cameraConfigs: CameraConfig[] = [
  {
    type: 'PerspectiveCamera',
    shortName: 'persp',
    cameraClass: 'PerspectiveCamera',
    params: perspectiveParams,
    buildCamera: (three, p) => new three.PerspectiveCamera(
      p.fov.value,
      1, // aspect set by renderer
      p.near.value,
      p.far.value
    ),
    updateCamera: (camera, p, changedParam) => {
      const cam = camera as PerspectiveCamera;
      if (changedParam === 'fov') {
        cam.fov = p.fov.value;
        cam.updateProjectionMatrix();
      } else if (changedParam === 'near') {
        cam.near = p.near.value;
        cam.updateProjectionMatrix();
      } else if (changedParam === 'far') {
        cam.far = p.far.value;
        cam.updateProjectionMatrix();
      }
    }
  },
  {
    type: 'OrthographicCamera',
    shortName: 'ortho',
    cameraClass: 'OrthographicCamera',
    params: orthographicParams,
    buildCamera: (three, p) => {
      const size = p.orthoSize.value;
      return new three.OrthographicCamera(
        -size, size, size, -size,
        p.near.value,
        p.far.value
      );
    },
    updateCamera: (camera, p, changedParam) => {
      const cam = camera as OrthographicCamera;
      if (changedParam === 'orthoSize') {
        const size = p.orthoSize.value;
        cam.top = size;
        cam.bottom = -size;
        cam.left = -size;
        cam.right = size;
        cam.updateProjectionMatrix();
      } else if (changedParam === 'near') {
        cam.near = p.near.value;
        cam.updateProjectionMatrix();
      } else if (changedParam === 'far') {
        cam.far = p.far.value;
        cam.updateProjectionMatrix();
      }
    }
  }
];

// ─────────────────────────────────────────────────────────────
// Generate Camera Nodes from Config
// ─────────────────────────────────────────────────────────────

export const cameraNodes: NodeDef[] = cameraConfigs.map((config): NodeDef => {
  const endpoints = {
    ...baseInternals,
    ...orbitParams,
    ...clipParams,
    ...config.params
  };

  return {
    type: config.type,
    shortName: config.shortName,
    category: 'camera',
    endpoints,
    ctor: class extends BaseNode<typeof endpoints> {
      private three: Three;
      private camera: CameraType | null = null;
      private renderer: ThreeRenderer;
      private config = config;
      private unregisterRenderer: () => void;
      private registeredAnimates = new Map<INode, (dt?: number) => any>();
      /** Track graph version to detect upstream topology changes */
      private lastGraphVersion = -1;
      /** Per-frame callback that checks for graph topology changes */
      private versionCheckCallback = () => {
        const currentVersion = this.deps.graphIndex.version;
        if (currentVersion !== this.lastGraphVersion) {
          this.lastGraphVersion = currentVersion;
          this.updateAnimateRoots();
        }
      };

      /** Per-frame callback for useExp camera params (orbit, fov, etc.) */
      private cameraAnimateCallback = () => {
        if (!this.camera || !this.hasFunctionParams()) return;
        this.updateCameraPosition();
        for (const id of this.deps.endpointApi.getEndpointIds()) {
          const meta = this.deps.endpointApi.getMetadata(id);
          if (meta?.dataFlow === 'param' && id !== 'resetCamera' && id !== 'showAxes' && id !== 'showGrid') {
            this.config.updateCamera(this.camera!, this.params, id);
          }
        }
      };

      constructor(id: string, deps: ThreeDependencies) {
        super(id, deps);
        this.three = deps.three;
        this.renderer = new ThreeRenderer(this.three);
        deps.endpointApi.registerRenderer(this.renderer);
        this.unregisterRenderer = deps.endpointApi.unregisterRenderer;
      }

      initialize(): void {
        super.initialize();
        this.buildCamera();
        this.renderer.showAxes = this.params.showAxes.value;
        this.renderer.showGrid = this.params.showGrid.value;
        // Register per-frame version check for animate root refresh
        this.renderer.addBeforeRender(this.versionCheckCallback);
        // Register per-frame useExp param animation
        this.renderer.addBeforeRender(this.cameraAnimateCallback);
      }

      private buildCamera(): void {
        if (!this.three) return;
        this.camera = this.config.buildCamera(this.three, this.params);
        this.updateCameraPosition();
        this.renderer.camera = this.camera;
      }

      private updateCameraPosition(): void {
        if (!this.camera) return;

        const theta = this.params.theta.value;
        const phi = this.params.phi.value;
        const roll = this.params.roll.value;
        const radius = this.params.radius.value;
        const panX = this.params.panX.value;
        const panY = this.params.panY.value;
        const panZ = this.params.panZ.value;

        // Spherical to Cartesian conversion (relative to pan target)
        const relX = radius * Math.sin(phi) * Math.cos(theta);
        const relY = radius * Math.cos(phi);
        const relZ = radius * Math.sin(phi) * Math.sin(theta);

        // Camera position = pan target + orbit offset
        const x = panX + relX;
        const y = panY + relY;
        const z = panZ + relZ;

        this.camera.position.set(x, y, z);

        // Calculate custom up vector based on roll
        // Default up is world Y, roll rotates it around the view axis
        if (Math.abs(roll) > 0.001) {
          // View direction (from camera to target, normalized)
          const viewX = -relX / radius;
          const viewY = -relY / radius;
          const viewZ = -relZ / radius;

          // Default up vector (world Y)
          let upX = 0, upY = 1, upZ = 0;

          // Rodrigues rotation: rotate up vector around view axis by roll angle
          const cos = Math.cos(roll);
          const sin = Math.sin(roll);

          // Cross product: view × up
          const crossX = viewY * upZ - viewZ * upY;
          const crossY = viewZ * upX - viewX * upZ;
          const crossZ = viewX * upY - viewY * upX;

          // Dot product: view · up
          const dot = viewX * upX + viewY * upY + viewZ * upZ;

          // Rotated up vector
          const newUpX = upX * cos + crossX * sin + viewX * dot * (1 - cos);
          const newUpY = upY * cos + crossY * sin + viewY * dot * (1 - cos);
          const newUpZ = upZ * cos + crossZ * sin + viewZ * dot * (1 - cos);

          this.camera.up.set(newUpX, newUpY, newUpZ);
        } else {
          this.camera.up.set(0, 1, 0);
        }

        this.camera.lookAt(panX, panY, panZ);

        // Ensure aspect ratio matches canvas if available
        const canvas = this.renderer.getCanvas?.();
        if (canvas && 'isPerspectiveCamera' in this.camera) {
          const cam = this.camera as import('three').PerspectiveCamera;
          const aspect = canvas.width / canvas.height;
          if (aspect > 0 && isFinite(aspect)) {
            cam.aspect = aspect;
          }
        }
        this.camera.updateProjectionMatrix();
      }

      onInternalChanged(payload: Payload, endpointId: string): void {
        if (endpointId === 'sceneIn') {
          this.renderer.scene = payload.value;
          // Re-apply grid setting now that scene is available
          if (this.params.showGrid.value) {
            this.renderer.showGrid = true;
          }
          this.updateAnimateRoots();
        }
      }

      onConnectedFrom(endpointId: string, _source: IEndpoint): void {
        if (endpointId === 'sceneIn') {
          this.updateAnimateRoots();
        }
      }

      onDisconnectedFrom(endpointId: string, _source: IEndpoint): void {
        if (endpointId === 'sceneIn') {
          this.updateAnimateRoots();
        }
      }

      private updateAnimateRoots(): void {
        // Remove old registrations
        for (const [, fn] of this.registeredAnimates) {
          this.renderer.removeBeforeRender(fn);
        }
        this.registeredAnimates.clear();

        // Find upstream nodes that need per-frame updates (SceneNode).
        // SceneNode's animate() handles version checks, realize,
        // and running upstream animate callbacks (useExp transforms, etc.)
        const perFrameNodes = this.deps.graphIndex.findUpstream(
          this.id, this.deps.instancePath,
          (node) => (node as any).needsPerFrameUpdate === true
        );

        for (const node of perFrameNodes) {
          const callback = () => node.animate();
          this.renderer.addBeforeRender(callback);
          this.registeredAnimates.set(node, callback);
        }
      }

      onParamChanged(_payload: Payload, endpointId: string): void {
        if (!this.camera) return;

        if (endpointId === 'resetCamera' && this.params.resetCamera.value) {
          // Reset all orbit/pan params to defaults
          this.params.theta.set(DEFAULT_THETA);
          this.params.phi.set(DEFAULT_PHI);
          this.params.roll.set(0);
          this.params.radius.set(DEFAULT_RADIUS);
          this.params.panX.set(0);
          this.params.panY.set(0);
          this.params.panZ.set(0);
          this.params.resetCamera.set(false); // Turn off the toggle
          this.updateCameraPosition();
        } else if (['theta', 'phi', 'roll', 'radius', 'panX', 'panY', 'panZ'].includes(endpointId)) {
          this.updateCameraPosition();
        } else if (endpointId === 'showAxes') {
          this.renderer.showAxes = this.params.showAxes.value;
        } else if (endpointId === 'showGrid') {
          this.renderer.showGrid = this.params.showGrid.value;
        } else {
          this.config.updateCamera(this.camera, this.params, endpointId);
        }
      }

      dispose(): void {
        this.renderer.removeBeforeRender(this.versionCheckCallback);
        this.renderer.removeBeforeRender(this.cameraAnimateCallback);
        for (const [, fn] of this.registeredAnimates) {
          this.renderer.removeBeforeRender(fn);
        }
        this.registeredAnimates.clear();
        this.unregisterRenderer();
        this.camera = null;
        super.dispose();
      }
    }
  };
});
